import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { DayLoad as DayLoadValue } from '@horizon/shared';

/**
 * Показывает нагрузку и мягко предупреждает о перегрузке, но никогда не
 * запрещает и не окрашивает день в тревожный цвет.
 *
 * Длительность выводится сокращениями («ч», «мин»), которые не склоняются:
 * после предлога «из» полная форма потребовала бы родительного падежа,
 * а собирать фразу из кусков со склонением по числу нельзя.
 */
export function DayLoad({ load }: { load: DayLoadValue }) {
  const { t } = useTranslation();
  if (load.plannedMinutes === 0 && !load.hasUnestimated) return null;

  const notes = [
    load.hasUnestimated ? t('today.loadPartial') : null,
    load.overLimit ? t('today.loadOver') : null,
  ].filter(Boolean);

  return (
    <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted">
      <span className="text-ink-2">
        {t('today.load', {
          planned: formatDuration(load.plannedMinutes, t),
          limit: formatDuration(load.limitMinutes, t),
        })}
      </span>
      {notes.map((note) => (
        <span key={note}>{note}</span>
      ))}
    </p>
  );
}

function formatDuration(minutes: number, t: TFunction): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t('common.shortMinutes', { value: m });
  if (m === 0) return t('common.shortHours', { value: h });
  return t('common.shortHoursMinutes', { hours: h, minutes: m });
}
